import type { FC } from 'react';
import React, { useCallback } from 'react';
import type { WrappedComponentProps } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenFilledIcon from '@atlaskit/icon/core/eye-open-filled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { LoadableAfterPaint } from '@confluence/loadable';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	UNIVERSAL_WATCH_CONTENT_SHORTCUT,
	UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { WatchButtonPlaceholder } from '@confluence/watch-dialog/entry-points/WatchButtonPlaceholder.js';
import { isSafari } from '@confluence/browser-helper';

const i18n = defineMessages({
	startWatch: {
		id: 'content-types-header.start.watch',
		defaultMessage: 'Watch',
		description: 'Text for the start watch button in content types header',
	},
	stopWatch: {
		id: 'content-types-header.stop.watch',
		defaultMessage: 'Stop watching',
		description: 'Text for the stop button in content types header',
	},
});

type TriggerProps = {
	isWatching: boolean;
	isSelected: boolean;
	onClick: () => void;
	onMouseOver: () => void;
	triggerProps?: any;
	contentType: string;
};

export const ContentTypesWatchDialogButton: FC<WrappedComponentProps & TriggerProps> = ({
	isWatching,
	isSelected,
	onClick,
	onMouseOver,
	triggerProps,
	contentType,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleWatchButton = useCallback(async () => {
		onClick();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'content-watch-button',
				source: 'viewedContentScreen',
				attributes: {
					contentType,
				},
			},
		}).fire();
	}, [onClick, createAnalyticsEvent, contentType]);

	const icon = isWatching ? (
		<EyeOpenFilledIcon label={intl.formatMessage(i18n.stopWatch)} />
	) : (
		<EyeOpenIcon label={intl.formatMessage(i18n.startWatch)} />
	);

	const tooltipContent = isWatching
		? intl.formatMessage(i18n.stopWatch)
		: intl.formatMessage(i18n.startWatch);

	const tooltipShortcut = useShortcutStrings({
		shortcut: isSafari()
			? UNIVERSAL_WATCH_CONTENT_SHORTCUT_SAFARI
			: UNIVERSAL_WATCH_CONTENT_SHORTCUT,
	});

	return (
		<Tooltip content={tooltipContent} shortcut={tooltipShortcut} position="top" hideTooltipOnClick>
			<Button
				onClick={handleWatchButton}
				onMouseOver={onMouseOver}
				appearance="subtle"
				iconBefore={icon}
				isSelected={isSelected}
				{...triggerProps}
				data-testId="watch-content-button"
			/>
		</Tooltip>
	);
};

export const ContentTypesWatchDialogButtonLoader = LoadableAfterPaint<any>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentTypesWatchDialogButton" */ './ContentTypesWatchDialogButton.tsx'
			)
		).ContentTypesWatchDialogButton,
	loading: WatchButtonPlaceholder,
});
